import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { useAuth } from '../../context/AuthContext';

const { FiCheck, FiArrowRight, FiMusic, FiLayers, FiShield, FiUser } = FiIcons;

const OnboardingFlow = ({ onComplete }) => {
  const { user, refetchProfile } = useAuth();
  const [step,    setStep]    = useState(1);
  const [type,    setType]    = useState(null); // 'artist' | 'fan'
  const [loading, setLoading] = useState(false);

  const nextStep = () => setStep(s => s + 1);

  const handleComplete = async () => {
    if (!user) return;
    setLoading(true);
    try {
      /* Map onboarding choice → valid DB role */
      const roleMap = { artist: 'artist', fan: 'fan' };
      const role    = roleMap[type] || 'fan';

      await supabase
        .from('d2f_profiles')
        .update({ onboarded: true, role })
        .eq('id', user.id);

      await refetchProfile();
      onComplete();
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { title: 'Who are you?',  icon: FiUser   },
    { title: 'Verification',  icon: FiShield },
    { title: 'Customize',     icon: FiLayers },
    { title: 'Finished',      icon: FiCheck  },
  ];

  return (
    <div className="max-w-xl mx-auto py-20 px-4">
      {/* Progress */}
      <div className="flex justify-between mb-12 relative">
        <div className="absolute top-4 left-0 w-full h-[2px] bg-zinc-800 -z-10" />
        {steps.map((s, i) => (
          <div key={i} className={`flex flex-col items-center gap-2 ${step > i ? 'text-white' : 'text-zinc-700'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
              step > i ? 'border-white bg-white text-black' : 'border-zinc-800 bg-black'
            }`}>
              {step > i + 1 ? <SafeIcon icon={FiCheck} className="text-sm" /> : <span className="text-sm font-bold">{i + 1}</span>}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:block">{s.title}</span>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1 — Choose role */}
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
            <h2 className="text-4xl font-black uppercase tracking-tighter text-white">Choose your path</h2>
            <div className="grid grid-cols-1 gap-4">
              {[
                { key: 'artist', icon: FiMusic,  title: 'Independent Artist', desc: 'Direct-to-fan drops, 100% control.' },
                { key: 'fan',    icon: FiUser,   title: 'Fan / Supporter',    desc: 'Discover, support, and connect directly.' },
              ].map(({ key, icon, title, desc }) => (
                <button key={key}
                  onClick={() => { setType(key); nextStep(); }}
                  className="p-6 border-2 border-zinc-800 hover:border-white transition-all rounded-xl text-left group">
                  <div className="flex justify-between items-center mb-2">
                    <SafeIcon icon={icon} className="text-2xl text-white" />
                    <SafeIcon icon={FiArrowRight} className="opacity-0 group-hover:opacity-100 transition-opacity text-white" />
                  </div>
                  <h4 className="font-bold text-xl uppercase text-white">{title}</h4>
                  <p className="text-zinc-500 text-sm">{desc}</p>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-zinc-700 font-bold uppercase tracking-widest text-center">
              Label Admin &amp; Platform Admin roles are assigned by the platform
            </p>
          </motion.div>
        )}

        {/* Step 2 — Verification */}
        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
            <h2 className="text-4xl font-black uppercase tracking-tighter text-white">Identity Check</h2>
            <p className="text-zinc-500 italic">We verify creators to prevent impersonation.</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">Social Link (Spotify / Instagram)</label>
                <input type="url"
                  className="w-full bg-zinc-900 border-2 border-zinc-800 rounded-lg p-4 mt-2 text-white focus:outline-none focus:border-white transition-colors"
                  placeholder="https://…" />
              </div>
              <button onClick={nextStep}
                className="w-full py-4 bg-white text-black font-bold rounded-lg uppercase tracking-widest hover:bg-zinc-200 transition-colors mt-4">
                Verify &amp; Continue
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3 — Vibe */}
        {step === 3 && (
          <motion.div key="step3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
            <h2 className="text-4xl font-black uppercase tracking-tighter text-white">Your Platform Vibe</h2>
            <div className="grid grid-cols-3 gap-4">
              {['CLEAN', 'BRUTALIST', 'GRUNGE'].map(v => (
                <button key={v} onClick={nextStep}
                  className="aspect-square border-2 border-zinc-800 hover:border-white rounded-xl flex items-center justify-center font-bold text-xs text-white transition-colors">
                  {v}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 4 — Done */}
        {step === 4 && (
          <motion.div key="step4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6 text-center py-10">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_40px_rgba(255,255,255,0.3)]">
              <SafeIcon icon={FiCheck} className="text-5xl text-black" />
            </div>
            <h2 className="text-4xl font-black uppercase tracking-tighter text-white">You're All Set!</h2>
            <p className="text-zinc-400">Welcome to Vision Direct.</p>
            <button onClick={handleComplete} disabled={loading}
              className="w-full py-4 bg-white text-black font-bold rounded-lg uppercase tracking-widest mt-8 disabled:opacity-50 hover:bg-zinc-200 transition-colors">
              {loading ? 'Saving…' : 'Enter Platform'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingFlow;